import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";

/* NC-SHIELD — visible anti-cheat theatre (deterrent). Zero-cost: pure presentation,
   no credits spent. Real detection can plug into this surface later. */
export function ShieldCheck_({ compact = false }) {
  const CHECKS = [
    ["kernel integrity", "VERIFIED"], ["prohibited process scan", "0 FOUND"],
    ["cheat engine / memory editors", "NOT FOUND"], ["debugger attach watch", "ARMED"],
    ["module signatures", "VERIFIED"], ["injection watch", "ARMED"],
    ["network heartbeat", "SYNCED"], ["replay buffer", "RECORDING"],
  ];
  const [done, setDone] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setDone((d) => (d >= CHECKS.length ? d : d + 1)), compact ? 220 : 300);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="ncshield" className={compact ? "font-mono" : "font-mono w-full max-w-xl px-8"}>
      <div className="flex items-center gap-2.5 mb-3">
        <svg viewBox="0 0 24 24" className={compact ? "w-4 h-4" : "w-5 h-5"} fill="none" stroke="#34d399" strokeWidth="1.8">
          <path d="M12 2l8 3.5v5.2c0 5-3.4 9.6-8 11.3-4.6-1.7-8-6.3-8-11.3V5.5L12 2z" />
          <path d="M8.5 12l2.4 2.4L15.5 9.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className={`${compact ? "text-[9px]" : "text-[11px]"} tracking-[0.4em] uppercase text-emerald-400 font-bold`}>NC-Shield // anti-cheat active</p>
      </div>
      <div className="flex flex-col gap-1">
        {CHECKS.slice(0, done).map(([k, v], i) => (
          <p key={k} className={`${compact ? "text-[8px]" : "text-[10px]"} text-zinc-500 flex justify-between gap-6`} style={{ animation: "ncFadeIn 0.2s ease both" }}>
            <span><span className="text-zinc-700 mr-2">{">"}</span>{k}</span>
            <span className={v === "CLEAN" || v === "VERIFIED" ? "text-emerald-400" : "text-nc-blue"}>{v}</span>
          </p>
        ))}
        {done < CHECKS.length && <span className="inline-block w-2 h-3 bg-emerald-400/80 mt-1" style={{ animation: "ncBlink 0.6s steps(1) infinite" }} />}
        {done >= CHECKS.length && (
          <p className={`${compact ? "text-[8px]" : "text-[10px]"} text-emerald-400/80 mt-1.5 tracking-[0.2em] uppercase`} style={{ animation: "ncFadeIn 0.4s ease both" }}>
            0 violations · session is monitored & recorded
          </p>
        )}
      </div>
    </div>
  );
}
export const ShieldCheck = ShieldCheck_;

const BOOT_LINES = [
  "allocating city grid // 64 districts",
  "linking hub uplink — specpixel.com",
  "warming render cores // nanite + lumen",
  "loading citizen registry // NCDB",
  "audio director // metasound buses",
  "syncing world state with hub",
];

/* Cold boot → photosensitivity warning → CRT → terminal → NC-Shield → studio logo →
   wordmark → early access → MOTD (only when the city actually has new news since your
   last session). Cinematic letterbox bars frame the whole sequence. */
export function BootSequence({ onDone, audio, theme, boot }) {
  const { t } = useI18n();
  const variant = boot?.variant || "default";
  const accent = boot?.accent || theme?.accent || "#2E6BFF";
  const bootTitle = boot?.title || theme?.bootTitle || "NEW CITIZEN";
  const [phase, setPhase] = useState(-1); // -1 photosensitivity, 0 crt, 1 terminal, 2 shield, 3 studio, 4 wordmark, 5 early access, 6 motd
  const [lines, setLines] = useState(0);
  const [guard, setGuard] = useState(null); // Steam Guard verdict: {ok, code, kind, hard, message}
  const [freshNews, setFreshNews] = useState(null); // latest bulletin if it's new to this player
  const newsRef = useRef(null);
  const finishedRef = useRef(false);
  const prevPhase = useRef(-2); // -2 so the warning fade fires on mount
  const onDoneRef = useRef(onDone);
  const guardRef = useRef(guard);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => { guardRef.current = guard; }, [guard]);

  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (newsRef.current) localStorage.setItem("nc_last_news", newsRef.current.published_at);
    if (!guardRef.current?.hard) onDoneRef.current && onDoneRef.current();
  };
  const finishRef = useRef(finish);
  useEffect(() => { finishRef.current = finish; });

  // Smart MOTD: only interrupt the boot when there's a bulletin the player hasn't seen.
  useEffect(() => {
    api.get("/nc/bulletins").then((r) => {
      const latest = (r.data.bulletins || [])[0];
      if (!latest) return;
      const seen = localStorage.getItem("nc_last_news") || "";
      if (latest.published_at > seen) { newsRef.current = latest; setFreshNews(latest); }
    }).catch(() => {});
  }, []);

  // Steam Guard — real VPN/proxy/Tor screen against the join IP. VPN/Tor hard-blocks
  // the boot (ban-evasion protection); proxy/hosting shows a warning but lets them in.
  useEffect(() => {
    if (!localStorage.getItem("nc_token")) return; // signed-out preview — loading page re-checks at join
    api.get("/nc/steam/status").then((r) => setGuard(r.data)).catch(() => {});
  }, []);
  const guardBlocked = !!(guard && !guard.ok && guard.code === "vpn_blocked" && guard.hard);
  const guardWarn = !!(guard && !guard.ok && guard.code === "vpn_blocked" && !guard.hard);

  useEffect(() => {
    if (phase !== prevPhase.current && audio) {
      if (phase === 0) audio.crtOn(); // tube crackle + hum as the screen wakes
      if (phase === 2) { audio.dataBurst(8); setTimeout(() => audio.dataBurst(5), 1100); } // anti-cheat sweep
      if (phase >= 2) audio.whoosh(phase === 4 ? 0.16 : 0.12); // card sweeps + MOTD slide
      prevPhase.current = phase;
    }
  }, [phase, audio]);

  // terminal typing: keystroke cluster per line + confirm beep when it passes
  const prevLines = useRef(0);
  useEffect(() => {
    if (phase !== 1 || !audio || lines === prevLines.current) { prevLines.current = lines; return; }
    prevLines.current = lines;
    audio.teletype();
    setTimeout(() => audio.lineOk(), 150);
  }, [lines, phase, audio]);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(0), 3200),     // warning had its moment — CRT wakes
      setTimeout(() => setPhase(1), 3800),
      setTimeout(() => setPhase(2), 6200),
      setTimeout(() => setPhase(3), 8600),
      setTimeout(() => setPhase(4), 10400),
      setTimeout(() => setPhase(5), 12200),
      // MOTD gate at 16.6s: only parks on the card when the city actually has new news
      setTimeout(() => { if (newsRef.current) setPhase(6); else finishRef.current(); }, 16600),
      setTimeout(() => finishRef.current(), 23200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase !== 1) return;
    const t = setInterval(() => setLines((l) => (l >= BOOT_LINES.length ? l : l + 1)), 330);
    return () => clearInterval(t);
  }, [phase]);

  // HARD BLOCK — VPN/Tor detected on the join IP. Boot halts here; no menu access.
  if (guardBlocked) {
    return (
      <div data-testid="menu-boot" className="absolute inset-0 bg-black flex items-center justify-center overflow-hidden">
        <div data-testid="guard-vpn-block" className="w-full max-w-xl px-6" style={{ animation: "ncFadeUp 0.5s ease both" }}>
          <div className="border border-red-500/40 bg-[#0f0608]/95 p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-red-600 via-red-300 to-transparent" />
            <div className="flex items-center gap-3 mb-5">
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="#f87171" strokeWidth="1.8">
                <path d="M12 2l8 3.5v5.2c0 5-3.4 9.6-8 11.3-4.6-1.7-8-6.3-8-11.3V5.5L12 2z" />
                <path d="M9 9l6 6M15 9l-6 6" strokeLinecap="round" />
              </svg>
              <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-red-400 font-bold">NC-Shield // connection refused</p>
            </div>
            <div className="font-mono text-[11px] sm:text-xs leading-relaxed text-zinc-300 flex flex-col gap-3">
              <p className="text-red-300 uppercase tracking-[0.15em]">{guard.kind?.replace(/,/g, " · ")} connection detected</p>
              <p>VPN and Tor connections are not allowed in New Citizen. This protects the city from ban evasion and multi-account abuse.</p>
              <p className="text-zinc-500">Disconnect from your VPN/Tor and restart the hub to continue. Repeated attempts are logged against your Steam account.</p>
            </div>
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-600 uppercase mt-6">protected by nc-shield · specpixel anti-cheat</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="menu-boot" className={`absolute inset-0 bg-black flex items-center justify-center overflow-hidden ${variant === "crt" ? "nc-boot-crt" : ""}`}>
      {/* workshop boot packs: matrix glyph rain / CRT scanlines */}
      {variant === "matrix" && (
        <div data-testid="boot-matrix" className="absolute inset-0 pointer-events-none overflow-hidden opacity-25">
          {Array.from({ length: 28 }).map((_, i) => (
            <span key={i} className="absolute top-0 font-mono text-[11px] leading-4 whitespace-pre" style={{
              left: `${(i / 28) * 100}%`, color: accent, animation: `ncRain ${2.4 + (i % 5) * 0.7}s linear ${(i % 7) * 0.4}s infinite`,
              textShadow: `0 0 8px ${accent}`,
            }}>
              {"01ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑｺﾔﾗﾖﾀﾚｿﾈﾙ".split("").slice(i % 9, (i % 9) + 18).join("\n")}
            </span>
          ))}
          <style>{`@keyframes ncRain { from { transform: translateY(-110%); } to { transform: translateY(110vh); } }`}</style>
        </div>
      )}
      {variant === "crt" && (
        <div data-testid="boot-crt" className="absolute inset-0 pointer-events-none z-30" style={{
          backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.28) 0 2px, transparent 2px 4px)",
          animation: "ncCrtFlicker 3.2s ease-in-out infinite",
        }}>
          <style>{`@keyframes ncCrtFlicker { 0%,100% { opacity: 1; } 48% { opacity: 0.86; } 52% { opacity: 0.95; } }`}</style>
        </div>
      )}
      {/* cinematic letterbox */}
      <div className="absolute top-0 inset-x-0 h-[7vh] bg-black z-10 border-b border-white/5" style={{ animation: "ncBarTop 0.8s cubic-bezier(0.7,0,0.3,1) both" }} />
      <div className="absolute bottom-0 inset-x-0 h-[7vh] bg-black z-10 border-t border-white/5" style={{ animation: "ncBarBottom 0.8s cubic-bezier(0.7,0,0.3,1) both" }} />
      <style>{`@keyframes ncBarTop { from { transform: translateY(-100%); } to { transform: none; } } @keyframes ncBarBottom { from { transform: translateY(100%); } to { transform: none; } } @keyframes ncPhaseCut { from { opacity: 1; } to { opacity: 0; } }`}</style>

      {/* living backdrop — keeps the black phases from feeling like dead voids */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(120,160,255,0.55) 1px, transparent 1px)", backgroundSize: "100% 3px" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 50%, rgba(46,107,255,0.08), transparent 70%)" }} />
      {/* CRT switch flash on every phase cut — hides the hard swap */}
      <div key={`cut-${phase}`} className="absolute inset-0 pointer-events-none z-20" style={{ animation: "ncPhaseCut 0.26s ease-out both", background: "linear-gradient(180deg, transparent 46%, rgba(140,175,255,0.16) 50%, transparent 54%)" }} />
      {phase === -1 && (
        <div data-testid="boot-epilepsy-warning" className="w-full max-w-lg px-6" style={{ animation: "ncFadeIn 0.5s ease both" }}>
          <div className="border border-white/25 bg-black p-8 text-center">
            <p className="font-mono text-[10px] tracking-[0.5em] text-zinc-200 uppercase font-bold mb-4">Photosensitivity warning</p>
            <p className="font-mono text-[10px] sm:text-[11px] leading-relaxed text-zinc-400">
              A small percentage of people may experience seizures when exposed to certain lights, patterns or flashing imagery in video games. If you or anyone in your family has an epileptic condition, consult a doctor before playing.
            </p>
            <div className="my-4 h-px bg-white/10" />
            <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 uppercase leading-relaxed">
              New Citizen contains flashing lights, night-time driving and strobe-adjacent effects
            </p>
          </div>
        </div>
      )}

      {phase === 0 && (
        <div className="w-full flex items-center justify-center">
          <div className="h-[2px] bg-white shadow-[0_0_24px_4px_rgba(160,190,255,0.9)]" style={{ animation: "ncCrt 0.55s cubic-bezier(0.2,0.9,0.2,1) forwards" }} />
        </div>
      )}

      {phase === 1 && (
        <div className="w-full max-w-xl px-8 font-mono text-[11px] sm:text-xs text-zinc-400" style={{ animation: "ncFadeIn 0.2s ease both" }}>
          <div className="flex items-center gap-3 mb-5">
            <img src="/assets/newcitizen-logo-blue.png" alt="" className="w-7 h-7 object-contain" />
            <p className="text-zinc-500 tracking-[0.3em]">{theme?.bootTitle || "NEW CITIZEN"} // {t("boot.system")}</p>
          </div>
          {BOOT_LINES.slice(0, lines).map((l, i) => (
            <p key={i} className="py-0.5 flex justify-between gap-4" style={{ animation: "ncFadeIn 0.15s ease both" }}>
              <span><span className="text-zinc-600 mr-2">{">"}</span>{l}</span>
              <span className="text-emerald-400">OK</span>
            </p>
          ))}
          <div className="mt-5 h-[3px] bg-white/10 overflow-hidden">
            <div className="h-full" style={{ backgroundColor: accent, boxShadow: `0 0 10px ${accent}`, animation: "ncProgress 2.2s cubic-bezier(0.3,0.8,0.4,1) both" }} />
          </div>
          <p className="mt-2 text-[9px] text-zinc-600 tracking-[0.25em]">{Math.min(100, Math.round((lines / BOOT_LINES.length) * 100))}%</p>
        </div>
      )}

      {phase === 2 && (
        <div className="flex flex-col items-center gap-4" style={{ animation: "ncFadeIn 0.3s ease both" }}>
          <ShieldCheck />
          <p className="font-mono text-[8px] tracking-[0.4em] text-zinc-600 uppercase">protected by nc-shield · specpixel anti-cheat</p>
        </div>
      )}

      {phase === 3 && (
        <div className="text-center px-6" style={{ animation: "ncLogoCard 2.1s ease both" }}>
          <img src="/assets/specpixel-logo-transparent.png" alt="Specpixel Entertainment" className="h-20 sm:h-28 object-contain mx-auto" style={{ animation: "ncGlitch 0.9s steps(2) both", filter: "drop-shadow(0 0 30px rgba(46,107,255,0.35))" }} />
          <p className="font-mono text-[10px] tracking-[0.6em] text-zinc-500 uppercase mt-5">Specpixel Entertainment</p>
        </div>
      )}

      {phase === 4 && (
        <div className="text-center px-6" style={{ animation: "ncLogoCard 1.9s ease both" }}>
          <p className="font-mono text-[10px] tracking-[0.6em] text-zinc-500 uppercase mb-6">{t("boot.original")}</p>
          <img src="/assets/newcitizen-wordmark.png" alt="New Citizen" className="h-12 sm:h-16 object-contain mx-auto" style={{ filter: "drop-shadow(0 0 34px rgba(46,107,255,0.45))" }} />
          <p className="font-mono text-[9px] tracking-[0.4em] text-zinc-600 uppercase mt-6">hub build 4.0 // unreal engine 5</p>
        </div>
      )}

      {phase === 5 && (
        <div data-testid="ea-notice" className="w-full max-w-2xl px-6" style={{ animation: "ncFadeUp 0.6s ease both" }}>
          <div className="border border-amber-500/25 bg-[#0c0a06]/90 p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-500 via-amber-200 to-transparent" />
            <p className="font-mono text-[9px] tracking-[0.5em] text-amber-400 uppercase mb-4">{t("ea.title")}</p>
            <div className="font-mono text-[11px] sm:text-xs leading-relaxed text-zinc-300 flex flex-col gap-3">
              <p>{t("ea.p1")}</p>
              <p className="text-zinc-100">{t("ea.p2")}</p>
            </div>
            <div className="flex items-center justify-between mt-6 gap-4 flex-wrap">
              <p className="font-mono text-[9px] tracking-[0.15em] text-amber-200/80 uppercase">{t("ea.thanks")}</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">— Specpixel Entertainment</p>
            </div>
          </div>
          <p className="text-center font-mono text-[9px] tracking-[0.4em] text-zinc-600 uppercase mt-4" style={{ animation: "ncPulse 1.4s ease-in-out infinite" }}>{t("boot.continue")}</p>
        </div>
      )}

      {phase === 6 && freshNews && (
        <div data-testid="motd-card" className="w-full max-w-2xl px-6" style={{ animation: "ncFadeUp 0.6s ease both" }}>
          <div className="border border-white/12 bg-[#080a0f]/90 p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#2E6BFF] via-[#ff8a2e] to-transparent" />
            <p className="font-mono text-[9px] tracking-[0.5em] text-[#2E6BFF] uppercase mb-4">{t("boot.motd")}</p>
            <div className="font-mono text-[11px] sm:text-xs leading-relaxed text-zinc-300 flex flex-col gap-3">
              <p data-testid="motd-news-title" className="text-zinc-100 font-bold">{freshNews.title}</p>
              <p>{freshNews.body?.slice(0, 300)}</p>
              {(freshNews.points || []).slice(0, 3).map((p, i) => (
                <p key={i} className="text-zinc-400">— {p}</p>
              ))}
              <p className="text-zinc-500 text-[10px]">— {freshNews.published_by} · {String(freshNews.published_at).slice(0, 10)}</p>
            </div>
            <div className="flex items-center justify-between mt-6">
              <img src="/assets/newcitizen-wordmark.png" alt="" className="h-5 object-contain opacity-80" />
              <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">— Specpixel Entertainment</p>
            </div>
          </div>
          <p className="text-center font-mono text-[9px] tracking-[0.4em] text-zinc-600 uppercase mt-4" style={{ animation: "ncPulse 1.4s ease-in-out infinite" }}>{t("boot.continue")}</p>
        </div>
      )}

      {/* WARN ONLY — proxy/hosting IP: let them in, but tell them it's been logged */}
      {guardWarn && phase >= 5 && (
        <div data-testid="guard-vpn-warn" className="absolute top-[8vh] inset-x-0 flex justify-center px-6 z-20" style={{ animation: "ncFadeIn 0.4s ease both" }}>
          <div className="border border-amber-500/35 bg-[#0c0a06]/95 px-5 py-2.5 flex items-center gap-3 max-w-2xl">
            <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none" stroke="#fbbf24" strokeWidth="1.8">
              <path d="M12 9v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.15em] uppercase text-amber-200/90">
              {guard.kind?.replace(/,/g, " · ")} connection detected — flagged & logged · repeated abuse leads to a ban
            </p>
          </div>
        </div>
      )}

      {phase >= 0 && phase < 5 && <p className="absolute bottom-8 inset-x-0 text-center font-mono text-[9px] tracking-[0.4em] text-zinc-500 uppercase" style={{ animation: "ncPulse 1.6s ease-in-out infinite" }}>{t("boot.skip")}</p>}
    </div>
  );
}
